import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query'

import { ANNOTATIONS_THREAD_COMMENTS } from '@/features/annotation-management/constants'
import { QUERY_KEY_COMMENT_LIST } from '@/features/comment/constants'
import { DOCUMENT_LIST_QUERY_KEY, useDocumentStore } from '@/features/document'
import { RequestCreateThreadOrReply } from '@/schema'
import { THREAD_AUTO_REFRESH_DELAY_MS } from '@/utils/constants.ts'

import {
  createComment,
  createReply,
  deleteComment,
  deleteThread,
  fetchTflHtml,
  getThreadComments,
  markThreadAsRead,
  resolveCommentThread,
  updateComment,
} from '../service'
import { useCommentStore } from '../store/commentStore'
import { CreateReplyRequest, UpdateCommentRequest } from '../types'

const QUERY_CONFIG = {
  META_STALE_TIME: 5 * 60 * 1000,
  HTML_STALE_TIME: 10 * 60 * 1000,
  TOC_STALE_TIME: 3 * 60 * 1000,
} as const

export const useTflHtml = (
  signedUrl?: string,
  fileId?: string,
  versionIdProp?: string
): UseQueryResult<string, Error> => {
  return useQuery({
    queryKey: ['tfl', 'html', fileId, versionIdProp],
    queryFn: async ({ signal }) => {
      if (!signedUrl) {
        throw new Error('Signed URL is required')
      }
      return fetchTflHtml(signedUrl, signal)
    },
    enabled: !!signedUrl,
    staleTime: Infinity,
  })
}

export const useAddComment = () => {
  const queryClient = useQueryClient()
  const activeDocumentId = useDocumentStore((state) => state.activeDocumentId)

  return useMutation({
    mutationFn: (data: RequestCreateThreadOrReply) => createComment(data),
    onSuccess: async (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: DOCUMENT_LIST_QUERY_KEY(),
        refetchType: 'active',
      })

      queryClient.invalidateQueries({
        queryKey: ANNOTATIONS_THREAD_COMMENTS([
          'thread-list',
          variables.document_version_id ?? '',
        ]),
      })

      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === QUERY_KEY_COMMENT_LIST,
      })

      if (activeDocumentId) {
        queryClient.invalidateQueries({
          queryKey: [
            'document-version',
            activeDocumentId,
            variables.document_version_id ?? '',
          ],
          refetchType: 'active',
        })
      }
    },
  })
}

export const useMarkCommentThreadResolved = (
  threadId: string,
  document_version_id: string
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (status: 'resolved' | 'open') =>
      resolveCommentThread(threadId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ANNOTATIONS_THREAD_COMMENTS([
          'thread-list',
          document_version_id,
        ]),
      })
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === QUERY_KEY_COMMENT_LIST,
      })
    },
  })
}

export const useDeleteCommentThread = (
  threadId: string,
  document_version_id: string
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => deleteThread(threadId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ANNOTATIONS_THREAD_COMMENTS([
          'thread-list',
          document_version_id,
        ]),
      })
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === QUERY_KEY_COMMENT_LIST,
      })
    },
  })
}

/**
 * Hook to fetch a thread with all its comments (root + replies)
 * @param threadId - Thread ID to retrieve
 * @returns Query result with thread comments
 */
export const useGetThreadComments = (threadId: string) => {
  return useQuery({
    queryKey: ['tfl', 'thread', threadId],
    queryFn: async () => {
      const response = await getThreadComments(threadId)
      return response.data
    },
    enabled: !!threadId,
    staleTime: QUERY_CONFIG.META_STALE_TIME,
    refetchInterval: THREAD_AUTO_REFRESH_DELAY_MS,
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
  })
}

/**
 * Hook to create a reply to an existing comment
 * @param documentVersionId - Document version ID for cache invalidation
 * @returns Mutation function to create reply
 */
export const useCreateReply = (documentVersionId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      threadId,
      data,
    }: {
      threadId: string
      data: CreateReplyRequest
    }) => createReply(threadId, data),
    onSuccess: (_response, variables) => {
      // thread list
      queryClient.invalidateQueries({
        queryKey: ANNOTATIONS_THREAD_COMMENTS([
          'thread-list',
          documentVersionId,
        ]),
      })
      queryClient.invalidateQueries({
        queryKey: ['tfl', 'thread', variables.threadId],
      })
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === QUERY_KEY_COMMENT_LIST,
      })
    },
  })
}

/**
 * Hook to update an existing comment's content and mentioned users
 * @param documentVersionId - Document version ID for cache invalidation
 * @returns Mutation function to update comment
 */
export const useUpdateComment = (documentVersionId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      commentId,
      data,
    }: {
      commentId: string
      data: UpdateCommentRequest
    }) => updateComment(commentId, data),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({
        queryKey: ANNOTATIONS_THREAD_COMMENTS([
          'thread-list',
          documentVersionId,
        ]),
      })
      const comment = queryClient.getQueryData<{ data: { thread_id: string } }>(
        ['tfl', 'comment', variables.commentId]
      )
      if (comment?.data?.thread_id) {
        queryClient.invalidateQueries({
          queryKey: ['tfl', 'thread', comment.data.thread_id],
        })
      }
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === QUERY_KEY_COMMENT_LIST,
      })
    },
  })
}

/**
 * Hook to delete a reply comment (soft delete)
 * @param documentVersionId - Document version ID for cache invalidation
 * @returns Mutation function to delete comment
 */
export const useDeleteComment = (documentVersionId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (commentId: string) => deleteComment(commentId),
    onSuccess: () => {
      // thread list
      queryClient.invalidateQueries({
        queryKey: ANNOTATIONS_THREAD_COMMENTS([
          'thread-list',
          documentVersionId,
        ]),
      })
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === QUERY_KEY_COMMENT_LIST,
      })
    },
  })
}

export const useMarkThreadAsRead = (
  documentVersionId: string,
  commentId: string
) => {
  const updateCommentReadStatus = useCommentStore(
    (state) => state.updateCommentReadStatus
  )

  return useMutation({
    mutationFn: (threadId: string) => markThreadAsRead(threadId),
    onSuccess: () => {
      updateCommentReadStatus(documentVersionId, commentId)
    },
  })
}
